import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkDeleteReports, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { ReportModel } from '../../../../types';
import Typography from '@mui/material/Typography';

interface ReportsDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  changeLogRoles: string[];
}

export default function ReportsDeleteDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
}: ReportsDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const reports = queryClient.getQueryData<ReportModel[] | undefined>([
    UserKeys.Reports,
  ]);

  const selectedReport = reports?.find(
    (report) => report.id === selectedRows[0]
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => bulkDeleteReports(selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The report has been deleted successfully!'
            : 'The reports have been deleted successfully!',
      });

      queryClient.setQueryData([UserKeys.Reports], (oldData: ReportModel[]) =>
        oldData.filter((report) => !selectedRows.includes(report.id))
      );

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue deleting the selected report. Please refresh and try again.'
            : 'There was an issue deleting the selected reports. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.report_center.reports.delete_reports, {
        module: ModuleList.modules.reportCenter.name,
        section: ModuleList.modules.reportCenter.sections.reports,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DeleteReport.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={selectedRows.length > 1 ? 'Delete Reports' : 'Delete Report'}
        dialogContent={
          <Typography>
            {selectedRows.length > 1 ? (
              <>
                {'Delete '}
                <strong>{selectedRows.length} selected reports</strong>
                {'?'}
              </>
            ) : (
              <>
                {'Delete report '}
                <strong>{selectedReport?.reportName}</strong>
                {'?'}
              </>
            )}
          </Typography>
        }
        primaryButton={{
          title: selectedRows.length > 1 ? 'Delete Reports' : 'Delete Report',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
